import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import { Events } from "@Context/common/general/context";
import { faLockAlt } from "@fortawesome/pro-solid-svg-icons";
import { Box, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { BorderGlow, BottomGradient, GlowOnHoverCard, NavCardBlur, NavCardMediumActionArea } from "./styled-components";
import { useExtractColors } from "react-extract-colors";
import CardStatusIcon from "./card-status-icon";

/**
 * This component is part of the VR app V2 redesign
 */

const TRANSITION_PROPS = "0.5s cubic-bezier(0.18, 0.9, 0.58, 1)";

interface INavCardMedium {
  title: string;
  subtitle: string;
  imgSrc: string;
  to: string;
  children?: React.ReactNode;
  disabled?: boolean;
}

const NavCardMedium = ({ title, subtitle, imgSrc, to, children, disabled }: INavCardMedium) => {
  const [rgb, setRgb] = useState<{ r: string; g: string; b: string }>({ r: "", g: "", b: "" });
  const [isHovered, setIsHovered] = useState(false);
  const { lighterColor } = useExtractColors(imgSrc);

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  const handleNavigate = () => {
    Events.app.navigate.emit({ path: to });
  };

  useEffect(() => {
    if (lighterColor) {
      const match = lighterColor.match(/\d+/g);
      if (match) {
        const [r, g, b] = match.map((n) => String(Math.ceil(Number(n) / 30) * 30));
        setRgb({ r, g, b });
      }
    }
  }, [lighterColor]);

  return (
    <Box sx={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center" }}>
      {disabled && (
        <>
          <NavCardBlur />
          <CardStatusIcon icon={faLockAlt} size={92} iconSize="2xl" />
        </>
      )}
      <BorderGlow isHovered={isHovered} r={rgb.r} g={rgb.g} b={rgb.b} />
      <Box sx={{ cornerShape: "squircle", borderRadius: "48px", overflow: "hidden", flexGrow: 1, position: "relative" }}>
        <Box sx={{ height: "480px", width: "318px" }}>
          <GlowOnHoverCard onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} isHovered={isHovered} r={rgb.r} g={rgb.g} b={rgb.b}>
            <HapticsButtonWrapper>
              <NavCardMediumActionArea onClick={handleNavigate} imageUrl={imgSrc} disabled={disabled}>
                <Stack sx={{ textAlign: "center", zIndex: 1 }}>
                  <Stack
                    spacing={1}
                    sx={{
                      alignItems: "center",
                      transition: `transform ${TRANSITION_PROPS}`,
                      transform: isHovered ? "translateY(-10px)" : "translateY(0)",
                    }}
                  >
                    {children}
                    <Typography variant="text4xl700" color="common.white">
                      {title}
                    </Typography>
                  </Stack>

                  <Typography
                    variant="textLg500"
                    color="dsNeutral.v300"
                    sx={{
                      opacity: isHovered ? 1 : 0,
                      transition: `opcaity ${TRANSITION_PROPS}`,
                    }}
                  >
                    {subtitle}
                  </Typography>
                </Stack>

                <BottomGradient />
              </NavCardMediumActionArea>
            </HapticsButtonWrapper>
          </GlowOnHoverCard>
        </Box>
      </Box>
    </Box>
  );
};

export default NavCardMedium;
